/**
 * AmbientDisplayPage — Full-screen ambient view route.
 *
 * DRR-183: Renders AmbientDisplay without AppShell.
 * Protected route — requires staff login.
 * Config from backend via useAmbientConfig (st-0-2 Setting).
 * Wires usePrimitiveData + real-time subscriptions to AmbientDisplay.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { Center, Loader, Stack, Text, MantineProvider, createTheme } from '@mantine/core';
import { AmbientDisplay } from '../components/ambient/AmbientDisplay';
import type { AlertTickerItem } from '../components/ambient/AlertTicker';
import { useAmbientConfig } from '../hooks/useAmbientConfig';
import { usePrimitiveData } from '../hooks/usePrimitiveData';
import { useAlertSubscriptions } from '../hooks/useRealTimeSubscriptions';
import { useAuth } from '../auth/AuthContext';

const ambientTheme = createTheme({
  primaryColor: 'blue',
  defaultRadius: 'md',
});

export function AmbientDisplayPage() {
  const { tenant } = useAuth();
  const { config, loading: configLoading } = useAmbientConfig();
  const { items, loading: dataLoading, error } = usePrimitiveData('task');

  // Real-time alert subscription
  const alerts = useAlertSubscriptions({
    tenant: tenant?.id,
    severityThreshold: config.alertSeverityThreshold,
  });

  // CRITICAL alert overlay state
  const [criticalAlert, setCriticalAlert] = useState<{
    message: string;
    timestamp: string;
  } | null>(null);
  const [autoDismissTimer, setAutoDismissTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  // Alert history for ticker
  const alertIdCounter = useRef(0);
  const [alertHistory, setAlertHistory] = useState<AlertTickerItem[]>([]);

  // Handle new alerts
  useEffect(() => {
    if (!alerts.latestUpdate) return;
    const update = alerts.latestUpdate;
    const alert = update.alert as Record<string, unknown>;
    const message = (alert.message as string) ?? (alert.description as string) ?? 'Safety event';
    const severity = update.isCritical ? 'CRITICAL' : ((alert.severity as string) ?? 'HIGH');

    alertIdCounter.current += 1;
    setAlertHistory((prev) => [
      ...prev.slice(-49),
      {
        id: `alert-${alertIdCounter.current}`,
        message,
        severity: severity as AlertTickerItem['severity'],
        timestamp: new Date(),
      },
    ]);

    if (update.isCritical) {
      setCriticalAlert({
        message,
        timestamp: new Date().toLocaleTimeString(),
      });

      if (autoDismissTimer) clearTimeout(autoDismissTimer);
      const dismissMs = (config.criticalOverlayDismissSeconds ?? 30) * 1000;
      const timer = setTimeout(() => setCriticalAlert(null), dismissMs);
      setAutoDismissTimer(timer);
    }
  }, [alerts.latestUpdate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDismissAlert = useCallback(() => {
    setCriticalAlert(null);
    if (autoDismissTimer) clearTimeout(autoDismissTimer);
  }, [autoDismissTimer]);

  if (configLoading || dataLoading) {
    return (
      <Center h="100vh">
        <Stack ta="center" gap="md">
          <Loader size="xl" type="dots" />
          <Text size="sm" c="dimmed">Loading ambient display...</Text>
        </Stack>
      </Center>
    );
  }

  if (error) {
    return (
      <Center h="100vh">
        <Text c="red" size="lg">
          Failed to load display data. Check connection.
        </Text>
      </Center>
    );
  }

  return (
    <MantineProvider theme={ambientTheme}>
      <AmbientDisplay
        items={items}
        config={config}
        isConnected={alerts.isConnected}
        criticalAlert={criticalAlert}
        onDismissAlert={handleDismissAlert}
        alertHistory={alertHistory}
      />
    </MantineProvider>
  );
}
